import { useEffect } from 'react'

import { useWebsocketContext } from '@/modules/websocketContext'
import type { DocumentDTO } from '../stores/types'
import { useDocumentsActions } from './documents'

type Actions = ReturnType<typeof useDocumentsActions>

interface EventData {
  action: WebsocketAction
  documents: DocumentDTO[]
  document: DocumentDTO | { id: number }
}

const HANDLERS = {
  FETCH_DOCUMENTS: (data: EventData, { addDocuments }: Actions) =>
    addDocuments(data.documents),
  CREATE_DOCUMENT: (data: EventData, { addDocument }: Actions) =>
    addDocument(data.document as DocumentDTO),
  UPDATE_DOCUMENT: (data: EventData, { setDocument }: Actions) =>
    setDocument(data.document as DocumentDTO),
  DELETE_DOCUMENT: (data: EventData, { deleteDocuments }: Actions) =>
    deleteDocuments([(data.document as { id: number }).id]),
}

type WebsocketAction = keyof typeof HANDLERS

export const useDocumentsWebsocket = () => {
  const { isReady, subscribe, unsubscribe } = useWebsocketContext()
  const actions = useDocumentsActions()

  useEffect(() => {
    if (!isReady) return

    const subscription = subscribe(
      { channel: 'AccountChannel' },
      {
        received: (data: unknown) => {
          const eventData = data as EventData
          const handler = HANDLERS[eventData.action]
          if (!handler) return
          handler(eventData, actions)
        },
      },
    )
    return () => unsubscribe(subscription)
  }, [isReady])
}
